import { useState } from "react";
import { Box, Button, Container, TextField, Typography, Stack, Card, CardContent, InputAdornment, IconButton, Paper, Grid } from "@mui/material";
import { login, register } from "../api/auth";
import { useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { motion } from "framer-motion";

import { useLocation } from "react-router-dom";
// ... imports

export default function LoginPage() {
  const nav = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<"login" | "register">(
      location.pathname === "/register" ? "register" : "login"
  );

  async function submit(e?: React.FormEvent) {
    if (e) e.preventDefault();
    setErr(null);
    try {
      if (mode === "register") {
        await register(username, password);
      }
      await login(username, password);
      nav("/");
    } catch (e: any) {
      let msg = e.message ?? "Failed";
      try {
          const parsed = JSON.parse(msg);
          if (parsed.error) msg = parsed.error;
      } catch {}
      setErr(msg);
    }
  }

  return (
    <Box sx={{ minHeight: 'calc(100vh - 65px)', display: 'flex', flexDirection: { xs: 'column', md: mode === 'login' ? 'row' : 'row-reverse' } }}>
        {/* Form Container */}
        <Box sx={{ 
            flex: { xs: 1, md: 0.5 }, 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            alignItems: 'center', 
            p: 4,
            bgcolor: 'background.default'
        }}>
            <Container maxWidth="xs">
                <Box sx={{ mb: 6 }}>
                    <Typography variant="h3" fontWeight={800} gutterBottom sx={{ background: 'linear-gradient(90deg, #2563eb, #9333ea)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        {mode === "login" ? "Welcome Back" : "Join the Community"}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {mode === "login" ? "Enter your details to rejoin the community." : "Create an account to join the community."}
                    </Typography>
                </Box>

                <form onSubmit={submit}>
                    <Stack spacing={3}>
                        <TextField 
                            label="Username" 
                            fullWidth 
                            value={username} 
                            onChange={(e) => setUsername(e.target.value)} 
                            variant="outlined"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                        />
                        <TextField
                            label="Password"
                            type={showPassword ? "text" : "password"}
                            fullWidth
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            variant="outlined"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                            InputProps={{
                                endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                    >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                                )
                            }}
                        />

                        {err && (
                            <Paper sx={{ p: 2, bgcolor: 'error.lighter', color: 'error.main', borderRadius: 2 }} elevation={0}>
                                <Typography variant="body2" textAlign="center" fontWeight={600} textTransform="capitalize">
                                    {err}
                                </Typography>
                            </Paper>
                        )}

                        <Button 
                            variant="contained" 
                            size="large" 
                            fullWidth 
                            type="submit"
                            disableElevation
                            sx={{ height: 56, borderRadius: 3, fontSize: '1.1rem', fontWeight: 700, background: 'linear-gradient(135deg, #2563eb, #9333ea)' }}
                        >
                            {mode === "login" ? "Sign In" : "Sign Up"}
                        </Button>
                    </Stack>
                </form>

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {mode === "login" ? "New to Threadly? " : "Already have an account? "}
                        <Box
                            component="span"
                            onClick={() => nav(mode === "login" ? "/register" : "/login")}
                            sx={{ 
                                fontWeight: 700, 
                                color: 'primary.main', 
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' } 
                            }}
                        >
                            {mode === "login" ? "Create an account" : "Sign in"}
                        </Box>
                    </Typography>
                </Box>
            </Container>
        </Box>

        {/* Visuals Container */}
        <Box sx={{ 
            display: { xs: 'none', md: 'flex' },
            flex: 0.5,
            bgcolor: 'background.paper',
            position: 'relative',
            overflow: 'hidden',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            {/* Background Gradient & Image */}
            <Box sx={{ position: 'absolute', inset: 0 }}>
                <img src="/forum_hero_illustration.png" alt="Background" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.8 }} 
                     onError={(e) => (e.currentTarget.src = 'https://source.unsplash.com/random/1920x1080?abstract')} />
                <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(37,99,235,0.7), rgba(147,51,234,0.7))', mixBlendMode: 'multiply' }} />
            </Box>

            <Box sx={{ position: 'relative', zIndex: 1, p: 8, color: 'white', maxWidth: 480 }}>
                <Typography variant="h2" fontWeight={800} sx={{ mb: 2 }}>
                    {mode === "login" ? "Join the conversation." : "Start your journey."}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                   Connect with diverse communities and discuss everything from daily life to professional passions. A platform for everyone.
                </Typography>
            </Box>
        </Box>
    </Box>
  );
}
